import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
import { Helmet } from 'react-helmet';
import {
  FaSearch,
  FaCrown,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaUsers,
  FaEye,
} from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';

// 🌐 TRANSLATION DICTIONARY (Ready for i18n libraries like react-i18next)
const translations = {
  en: {
    metaTitle: 'History | NotReal003',
    metaDesc: 'Explore the most searched players of NetherGames Network!',
    pageTitle: 'Most Popular Players',
    totalPlayers: 'Total Players',
    totalViews: 'Total Views',
    searchResults: 'Search Results',
    topViews: 'Top Views',
    searchPlaceholder: 'Search by name or xuid...',
    sortByViews: 'Sort by Views',
    sortByName: 'Sort by Name',
    mostPopular: 'Most Popular Player',
    views: 'Views:',
    showing: 'Showing',
    of: 'of',
    players: 'players',
    perPage: 'per page',
    noPlayersFound: 'No players found.',
    clearSearch: 'Clear Search',
    jumpToPage: 'Jump to page:',
    underMaintenance: 'Under Maintenance',
    failedToLoad: 'Failed to load players. Please try again later.',
    reload: 'Reload',
  }
};

const t = translations.en; // Swap this dynamically based on user language preference

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// Polished Skeleton Card
const SkeletonCard = () => (
  <div className="bg-slate-800/40 rounded-2xl p-6 shadow-lg border border-slate-700/50 animate-pulse">
    <div className="flex items-center gap-5">
      <div className="w-16 h-16 rounded-full bg-slate-700/80 shadow-inner" />
      <div className="flex-1 space-y-3">
        <div className="h-5 w-2/3 rounded-full bg-slate-700/80" />
        <div className="h-4 w-1/2 rounded-full bg-slate-700/60" />
        <div className="h-4 w-1/3 rounded-full bg-indigo-500/30" />
      </div>
    </div>
  </div>
);

// Memoized & Redesigned Player Card
const PlayerCard = React.memo(({ player, onClick }) => (
  <motion.div
    layout
    variants={{
      hidden: { opacity: 0, scale: 0.95, y: 20 },
      visible: { opacity: 1, scale: 1, y: 0 },
    }}
    initial="hidden"
    animate="visible"
    exit={{ opacity: 0, scale: 0.95, y: -20 }}
    transition={{ duration: 0.3 }}
    onClick={onClick}
    className="cursor-pointer bg-slate-900/40 border border-slate-700/50 backdrop-blur-md rounded-2xl p-6 hover:shadow-indigo-500/10 hover:border-indigo-500/50 hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden"
  >
    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    <div className="flex items-center gap-5 relative z-10">
      <img
        src={player.avatar}
        alt={player.name}
        className="w-16 h-16 rounded-full shadow-md border-2 border-slate-600 group-hover:border-indigo-400 transition-colors duration-300"
        loading="lazy"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="font-bold text-lg text-slate-100 group-hover:text-indigo-200 transition-colors truncate">
            {player.name}
          </p>
          <span className="text-xs font-bold px-2 py-1 rounded-md bg-slate-800 text-slate-400 group-hover:bg-indigo-500/20 group-hover:text-indigo-300 transition-colors">
            #{player.rank}
          </span>
        </div>
        <p className="text-xs text-slate-400 mt-1 truncate">xuid: {player.xuid} ({player.spamStrikes})</p>
        <p className="text-sm text-indigo-400 font-medium mt-2">
          {t.views}{' '}
          <CountUp end={player.searchCount} duration={1.5} separator="," preserveValue className="text-indigo-200" />
        </p>
      </div>
    </div>
  </motion.div>
));

// Refined Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage, onItemsPerPageChange }) => {
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-10 p-5 bg-slate-900/30 backdrop-blur-md rounded-2xl border border-slate-700/50">
      <div className="flex items-center gap-4 text-sm text-slate-400">
        <span>
          {t.showing} <span className="font-semibold text-slate-200">{startItem}-{endItem}</span> {t.of}{' '}
          <span className="font-semibold text-slate-200">{totalItems}</span> {t.players}
        </span>
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="bg-slate-800 text-slate-200 px-3 py-1.5 rounded-lg border border-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 cursor-pointer"
        >
          <option value={12}>12 {t.perPage}</option>
          <option value={24}>24 {t.perPage}</option>
          <option value={48}>48 {t.perPage}</option>
        </select>
      </div>

      <div className="flex items-center gap-1.5">
        {[
          { icon: <FaAngleDoubleLeft />, page: 1, disabled: currentPage === 1 },
          { icon: <FaChevronLeft />, page: currentPage - 1, disabled: currentPage === 1 },
        ].map((btn, idx) => (
          <button
            key={`prev-${idx}`}
            onClick={() => onPageChange(btn.page)}
            disabled={btn.disabled}
            className="p-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-700 transition-colors"
          >
            {btn.icon}
          </button>
        ))}

        <div className="flex items-center gap-1 px-2">
          {getPageNumbers().map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === 'number' && onPageChange(page)}
              disabled={page === '...'}
              className={`min-w-[40px] h-10 rounded-lg font-medium transition-all duration-200 ${
                page === currentPage
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/20 border border-indigo-500'
                  : page === '...'
                  ? 'bg-transparent text-slate-500 cursor-default'
                  : 'bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {page}
            </button>
          ))}
        </div>

        {[
          { icon: <FaChevronRight />, page: currentPage + 1, disabled: currentPage === totalPages },
          { icon: <FaAngleDoubleRight />, page: totalPages, disabled: currentPage === totalPages },
        ].map((btn, idx) => (
          <button
            key={`next-${idx}`}
            onClick={() => onPageChange(btn.page)}
            disabled={btn.disabled}
            className="p-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-700 transition-colors"
          >
            {btn.icon}
          </button>
        ))}
      </div>
    </div>
  );
};

// Stats Card
const StatsCard = ({ icon: Icon, label, value, colorClass }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-slate-900/40 rounded-2xl p-5 border border-slate-700/50 backdrop-blur-md flex items-center gap-4"
  >
    <div className={`p-3 rounded-xl ${colorClass} bg-opacity-10 border border-current`}>
      <Icon className="text-2xl" />
    </div>
    <div>
      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">{label}</p>
      <p className="text-xl font-bold text-slate-100">
        <CountUp end={value} duration={1.5} separator="," />
      </p>
    </div>
  </motion.div>
);

const PlayersPage = () => {
  const [players, setPlayers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('searchCount');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maintenance, setMaintenance] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const fetchPlayers = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get('https://api.notreal003.org/collect/players');
      setPlayers(res.data);
    } catch (error) {
      console.error('Error fetching players:', error);
      const serverMessage = `(${error.response?.status}) ${error.response?.data?.message}`;
      if (error.response?.status === 503) {
        setMaintenance(serverMessage || t.underMaintenance);
      }
      setError(serverMessage || error.message || t.failedToLoad);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPlayers();
    const pageParam = searchParams.get('page');
    if (pageParam) setCurrentPage(parseInt(pageParam, 10));
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, sortBy]);

  useEffect(() => {
    if (currentPage > 1) {
      setSearchParams({ page: currentPage.toString() });
    } else {
      setSearchParams({});
    }
  }, [currentPage, setSearchParams]);

  // OPTIMIZATION: Calculate global ranks ONLY when the players array changes.
  const globalRanks = useMemo(() => {
    const map = new Map();
    const rankedList = [...players].sort((a, b) => b.searchCount - a.searchCount);
    rankedList.forEach((player, index) => {
      map.set(player._id, index + 1);
    });
    return map;
  }, [players]);

  // OPTIMIZATION: Filter first, then sort the smaller dataset. Map the ranks efficiently.
  const filteredAndSortedPlayers = useMemo(() => {
    let result = players;
    
    if (debouncedSearchTerm) {
      const term = debouncedSearchTerm.toLowerCase();
      result = result.filter(player => 
        player.name?.toLowerCase().includes(term) || player.xuid?.includes(term)
      );
    }

    if (sortBy === 'name') {
      result = [...result].sort((a, b) => a.name.localeCompare(b.name));
    } else {
      result = [...result].sort((a, b) => b.searchCount - a.searchCount);
    }

    return result.map(player => ({
      ...player,
      rank: globalRanks.get(player._id),
    }));
  }, [players, debouncedSearchTerm, sortBy, globalRanks]);

  const totalPages = Math.ceil(filteredAndSortedPlayers.length / itemsPerPage);
  
  const paginatedPlayers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedPlayers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedPlayers, currentPage, itemsPerPage]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const mostSearched = useMemo(() => {
    if (players.length === 0) return null;
    return players.reduce((max, p) => (p.searchCount > max.searchCount ? p : max), players[0]);
  }, [players]);

  const totalViews = useMemo(() => players.reduce((sum, p) => sum + p.searchCount, 0), [players]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0B0F19] py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="h-10 w-64 mx-auto mb-12 rounded-lg bg-slate-800 animate-pulse" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        </div>
      </div>
    );
  }

  if (maintenance || error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0F19] text-white">
        <div className="text-center p-8 bg-slate-900/50 rounded-2xl border border-slate-800">
          <p className="text-xl mb-6 font-medium text-slate-300">{maintenance || error}</p>
          {error && (
            <button onClick={() => window.location.reload()} className="px-6 py-2.5 bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors">
              {t.reload}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t.metaTitle}</title>
        <meta property="og:title" content={t.metaTitle} />
        <meta property="description" content={t.metaDesc} />
        <meta property="og:url" content="https://notreal003.org/history" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://notreal003.org/og-history.PNG" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      
      <div className="min-h-screen bg-[#0B0F19] text-slate-200 py-12 px-6 font-sans">
        <div className="max-w-7xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-extrabold text-center mb-10 text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400"
          >
            {t.pageTitle}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
          >
            <StatsCard icon={FaUsers} label={t.totalPlayers} value={players.length} colorClass="text-indigo-400" />
            <StatsCard icon={FaEye} label={t.totalViews} value={totalViews} colorClass="text-blue-400" />
            <StatsCard icon={FaSearch} label={t.searchResults} value={filteredAndSortedPlayers.length} colorClass="text-purple-400" />
            <StatsCard icon={FaCrown} label={t.topViews} value={mostSearched?.searchCount || 0} colorClass="text-amber-400" />
          </motion.div>

          <div className="flex flex-col md:flex-row gap-4 mb-10">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative flex-1">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder={t.searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-12 py-3.5 rounded-xl bg-slate-900/50 border border-slate-700/50 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all shadow-sm"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  <FaTimes />
                </button>
              )}
            </motion.div>

            <motion.select
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full md:w-56 bg-slate-900/50 text-slate-200 px-4 py-3.5 rounded-xl border border-slate-700/50 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 cursor-pointer shadow-sm"
            >
              <option value="searchCount">{t.sortByViews}</option>
              <option value="name">{t.sortByName}</option>
            </motion.select>
          </div>

          {mostSearched && !debouncedSearchTerm && currentPage === 1 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={() => navigate(`/players/${encodeURIComponent(mostSearched.name)}`)}
              className="mb-10 p-8 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 cursor-pointer hover:border-amber-500/40 transition-colors relative overflow-hidden group"
            >
              <h2 className="text-xl font-bold mb-6 text-amber-400 flex items-center gap-2">
                <FaCrown /> {t.mostPopular}
              </h2>
              <div className="flex items-center gap-6 relative z-10">
                <img src={mostSearched.avatar} alt={mostSearched.name} className="w-20 h-20 rounded-full border-2 border-amber-500/30 group-hover:border-amber-400 transition-colors" />
                <div>
                  <p className="text-2xl font-bold text-slate-100">{mostSearched.name}</p>
                  <p className="text-sm text-slate-400 mt-1">xuid: {mostSearched.xuid}</p>
                  <p className="text-amber-300 font-semibold mt-2 flex items-center gap-2">
                    {t.views} <CountUp end={mostSearched.searchCount} separator="," />
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          <AnimatePresence mode="popLayout">
            <motion.div
              key={`${currentPage}-${sortBy}`}
              className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
            >
              {paginatedPlayers.map((player) => (
                <PlayerCard key={player._id} player={player} onClick={() => navigate(`/players/${encodeURIComponent(player.name)}`)} />
              ))}
            </motion.div>
          </AnimatePresence>

          {filteredAndSortedPlayers.length === 0 && (
            <div className="text-center mt-20">
              <p className="text-slate-500 text-lg mb-4">{t.noPlayersFound}</p>
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="px-6 py-2.5 bg-indigo-600/20 text-indigo-400 rounded-lg hover:bg-indigo-600/30 transition-colors">
                  {t.clearSearch}
                </button>
              )}
            </div>
          )}

          {filteredAndSortedPlayers.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalItems={filteredAndSortedPlayers.length}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(val) => { setItemsPerPage(val); setCurrentPage(1); }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default PlayersPage;
